import { useState } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '@/config/constants';
import {
  SparklesIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface AIContentButtonsProps {
  onContentGenerated: (content: string) => void;
  postId?: string;
  title?: string;
  category?: string;
  location?: string;
}

type PromptType = 'intro' | 'tips' | 'activities' | 'culture' | 'custom';

interface QuickPrompt {
  id: PromptType;
  label: string;
  prompt: string;
}

const AIContentButtons = ({
  onContentGenerated,
  postId,
  title,
  category,
  location,
}: AIContentButtonsProps) => {
  const [loading, setLoading] = useState(false);
  const [activePrompt, setActivePrompt] = useState<PromptType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  const quickPrompts: QuickPrompt[] = [
    {
      id: 'intro',
      label: 'Write an introduction paragraph',
      prompt: 'Write an engaging introduction paragraph',
    },
    {
      id: 'tips',
      label: 'Add practical tips and recommendations',
      prompt: 'Write practical tips, advice, and recommendations',
    },
    {
      id: 'activities',
      label: 'Create a detailed guide section',
      prompt: 'Create a detailed guide section with key points and steps',
    },
    {
      id: 'culture',
      label: 'Write insights and analysis',
      prompt: 'Write insightful analysis and interesting perspectives',
    },
  ];

  const generateContent = async (promptType: PromptType, prompt: string) => {
    try {
      setLoading(true);
      setActivePrompt(promptType);
      setError(null);

      const token = getAuthToken();

      // Build context-aware prompt
      let finalPrompt = prompt;
      if (title) {
        finalPrompt = `${prompt} for: ${title}`;
      }
      if (category) finalPrompt += ` (Category: ${category})`;
      if (location) finalPrompt += ` (Location: ${location})`;

      // Use the posts AI endpoint if we have a postId, otherwise use general AI endpoint
      const url = postId
        ? `${API_ENDPOINTS.posts}/${postId}/ai/generate`
        : `${API_ENDPOINTS.ai}/generate`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: finalPrompt,
          type: promptType === 'custom' ? 'custom' : 'content',
          context: { title, category, location },
        }),
      });

      const result = await response.json();

      if (result.success) {
        const content = result.data?.content || result.data;
        onContentGenerated(content);
        setShowCustom(false);
        setCustomPrompt('');
      } else {
        setError(result.error || 'Generation failed');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError('Failed to generate content. Please try again.');
    } finally {
      setLoading(false);
      setActivePrompt(null);
    }
  };

  const handleGenerateMain = () => {
    const prompt = title
      ? `Write detailed blog content about: ${title}`
      : 'Write engaging blog content about travel experiences';
    generateContent('intro', prompt);
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 border border-violet-200 rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-violet-100 rounded-lg">
            <SparklesIcon className="h-5 w-5 text-violet-600" />
          </div>
          <span className="font-semibold text-gray-800">
            AI Content Generator
          </span>
        </div>
        <button
          onClick={() => setShowCustom(!showCustom)}
          className="text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
        >
          {showCustom ? 'Quick Options' : 'Custom Prompt'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {!showCustom ? (
        <>
          {/* Main Generate Button */}
          <button
            onClick={handleGenerateMain}
            disabled={loading}
            className="w-full mb-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-violet-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
          >
            {loading &&
            activePrompt === 'intro' &&
            !quickPrompts.find(p => p.id === activePrompt) ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <PencilSquareIcon className="h-5 w-5" />
                <span>Generate Content</span>
              </>
            )}
          </button>

          {/* Quick Prompts Grid */}
          <div className="grid grid-cols-2 gap-2">
            {quickPrompts.map(item => (
              <button
                key={item.id}
                onClick={() => generateContent(item.id, item.prompt)}
                disabled={loading}
                className="text-left text-sm bg-white/70 backdrop-blur-sm border border-violet-200 text-violet-700 py-2.5 px-3 rounded-lg hover:bg-white hover:border-violet-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
              >
                {loading && activePrompt === item.id ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-violet-600 border-t-transparent" />
                    Generating...
                  </span>
                ) : (
                  item.label
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        /* Custom Prompt Section */
        <div className="space-y-3">
          <textarea
            value={customPrompt}
            onChange={e => setCustomPrompt(e.target.value)}
            placeholder="Enter your custom prompt (e.g., 'Write about the best restaurants and local cuisine')"
            className="w-full px-4 py-3 bg-white border border-violet-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm resize-none placeholder:text-gray-400"
            rows={3}
          />
          <button
            onClick={() => generateContent('custom', customPrompt)}
            disabled={loading || !customPrompt.trim()}
            className="w-full bg-violet-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading && activePrompt === 'custom' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Generate with Custom Prompt</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Context info */}
      {(title || category || location) && (
        <div className="mt-4 pt-3 border-t border-violet-200/50">
          <p className="text-xs text-gray-500 mb-1.5">
            AI will use this context:
          </p>
          <div className="flex flex-wrap gap-2">
            {title && (
              <span className="text-xs bg-white/80 text-gray-600 px-2 py-1 rounded-md border border-gray-200">
                {title.length > 30 ? title.slice(0, 30) + '...' : title}
              </span>
            )}
            {category && (
              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-md">
                {category}
              </span>
            )}
            {location && (
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-md">
                {location}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIContentButtons;
